import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Chip,
	CircularProgress,
	Divider,
	Stack,
	Typography,
} from "@mui/material";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import dayjs from "dayjs";

import { RateCardItem, useRateCard } from "@/hooks/api/use-rate-card";

import { EditItemDialog } from "./edit-item-dialog";
import { formatPrice, SECTIONS } from "./format";

/** The rate card every client sees on the request form, for super-admins to keep current. */
export function RateCard(): React.JSX.Element {
	const { data: items, isLoading, isError } = useRateCard();
	const [editing, setEditing] = React.useState<RateCardItem | null>(null);

	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Box>
					<Typography variant="h4">Rate card</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						One card for every client, shown on the campaign request form. All prices exclude GST. Lines marked optional
						can be ticked by clients as extra services.
					</Typography>
				</Box>

				{isLoading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
						<CircularProgress />
					</Box>
				) : null}
				{isError ? <Alert severity="error">The rate card could not be loaded.</Alert> : null}

				{SECTIONS.map((section) => {
					const lines = (items ?? []).filter((item) => item.section === section.value);
					if (lines.length === 0) return null;
					return (
						<Card key={section.value}>
							<CardHeader title={section.label} subheader={section.note} />
							<Divider />
							<CardContent>
								<Stack divider={<Divider />}>
									{lines.map((item) => (
										<Stack
											key={item.id}
											direction={{ xs: "column", sm: "row" }}
											spacing={2}
											sx={{ py: 1.5, alignItems: { sm: "flex-start" } }}
										>
											<Box sx={{ flex: "1 1 auto" }}>
												<Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
													<Typography variant="subtitle2">{item.name}</Typography>
													{item.optional ? (
														<Chip label="Optional" size="small" color="primary" variant="outlined" />
													) : null}
												</Stack>
												{item.description ? (
													<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
														{item.description}
													</Typography>
												) : null}
												{item.updatedByName ? (
													<Typography variant="caption" color="text.secondary">
														Last changed by {item.updatedByName}, {dayjs(item.updatedAt).format("D MMM YYYY")}
													</Typography>
												) : null}
											</Box>
											<Typography
												variant="subtitle2"
												sx={{ whiteSpace: "nowrap", minWidth: 160, textAlign: { sm: "right" } }}
											>
												{formatPrice(item)}
											</Typography>
											<Button size="small" startIcon={<PencilSimpleIcon />} onClick={() => setEditing(item)}>
												Edit
											</Button>
										</Stack>
									))}
								</Stack>
							</CardContent>
						</Card>
					);
				})}
			</Stack>

			<EditItemDialog item={editing} onClose={() => setEditing(null)} />
		</Box>
	);
}
